import { supabase } from '@/integrations/supabase/client';
import { cleanTextForSpeech } from '@/hooks/speech/speechUtils';

export interface AudioState {
  isPlaying: boolean;
  isPaused: boolean;
  isStopped: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  currentWordIndex: number;
  error: string | null;
  isReady: boolean;
  bufferingProgress: number; // PHASE 4: Track buffering progress
}

export interface AudioConfig {
  rate: number;
  pitch: number;
  volume: number;
  voice: string;
}

export interface TextSegment {
  text: string;
  startTime: number;
  endTime: number;
  wordIndex: number;
}

type StateChangeCallback = (state: AudioState) => void;
type WordChangeCallback = (wordIndex: number) => void;

export class UnifiedAudioEngine {
  private utterance: SpeechSynthesisUtterance | null = null;
  private state: AudioState = {
    isPlaying: false,
    isPaused: false,
    isStopped: true,
    currentTime: 0,
    duration: 0,
    progress: 0,
    currentWordIndex: 0,
    error: null,
    isReady: true, // TTS is always ready immediately
    bufferingProgress: 100, // TTS doesn't buffer - always 100%
  };
  private config: AudioConfig = {
    rate: 1,
    pitch: 1,
    volume: 0.8,
    voice: '',
  };
  private text: string = '';
  private words: string[] = [];
  private stateCallbacks: StateChangeCallback[] = [];
  private wordCallbacks: WordChangeCallback[] = [];
  private progressInterval: number | null = null;
  private startTime: number = 0;
  private pausedTime: number = 0;
  private moduleId: string = '';
  private sectionId: string = '';
  private lastSavedProgress: number = 0;
  private visibilityHandler: (() => void) | null = null;
  private wasPlayingBeforeHidden: boolean = false;
  private chunks: string[] = [];
  private currentChunkIndex: number = 0;

  constructor() {
    this.setupVisibilityListener();
  }

  private setupVisibilityListener() {
    this.visibilityHandler = () => {
      if (document.hidden) {
        // Tab is hidden - auto pause if playing
        if (this.state.isPlaying && !this.state.isPaused) {
          console.log('🔇 Tab hidden - auto pausing audio');
          this.wasPlayingBeforeHidden = true;
          this.pause();
        }
      } else {
        // Tab is visible - auto resume if it was playing before
        if (this.wasPlayingBeforeHidden && this.state.isPaused) {
          console.log('👀 Tab visible - auto resuming audio');
          this.wasPlayingBeforeHidden = false;
          setTimeout(() => this.play(), 100);
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  public initialize(text: string, moduleId: string, sectionId: string, config?: Partial<AudioConfig>) {
    console.log('🎬 Initializing audio engine:', { 
      textLength: text.length, 
      moduleId, 
      sectionId,
      config 
    });
    
    // Clean text to remove markdown/HTML before processing
    const cleanedText = cleanTextForSpeech(text);
    console.log('🧹 Cleaned text:', { original: text.length, cleaned: cleanedText.length });
    
    this.text = cleanedText;
    this.moduleId = moduleId;
    this.sectionId = sectionId;
    this.words = this.splitIntoWords(cleanedText);
    
    console.log('📝 Words split:', this.words.length);
    
    if (config) {
      this.config = { ...this.config, ...config };
    }

    // Calculate estimated duration (words per minute based on rate)
    const wordsPerMinute = 150 * this.config.rate;
    this.state.duration = (this.words.length / wordsPerMinute) * 60;
    
    console.log('⏱️ Estimated duration:', this.state.duration, 'seconds');
    
    this.loadLastPosition();
  }

  private async loadLastPosition() {
    if (!this.moduleId || !this.sectionId) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('audio_progress')
      .select('last_position, progress')
      .eq('user_id', user.id)
      .eq('module_id', this.moduleId)
      .eq('section_id', this.sectionId)
      .single();

    if (data) {
      this.pausedTime = data.last_position || 0;
      this.state.currentTime = this.pausedTime;
      this.state.progress = data.progress || 0;
      this.updateState();
    }
  }

  private async saveProgress(position: number, progress: number) {
    if (!this.moduleId || !this.sectionId) return;
    if (Math.abs(progress - this.lastSavedProgress) < 1) return; // Don't save if less than 1% change

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    this.lastSavedProgress = progress;

    const { error } = await supabase
      .from('audio_progress')
      .upsert({
        user_id: user.id,
        module_id: this.moduleId,
        section_id: this.sectionId,
        last_position: Math.round(position),
        progress: Math.round(progress),
        duration: Math.round(this.state.duration),
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,module_id,section_id'
      });

    if (error) {
      console.error('❌ Error saving progress:', error);
    } else {
      console.log('✅ Progress saved:', { position: Math.round(position), progress: Math.round(progress) });
    }
  }

  private splitIntoWords(text: string): string[] {
    return text
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .filter(word => word.length > 0);
  }

  private splitIntoChunks(text: string, maxChunkSize: number = 3000): string[] {
    const chunks: string[] = [];
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    let currentChunk = '';
    
    for (const sentence of sentences) {
      if ((currentChunk + sentence).length > maxChunkSize && currentChunk.length > 0) {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk += sentence;
      }
    }
    
    if (currentChunk.trim().length > 0) {
      chunks.push(currentChunk.trim());
    }
    
    return chunks.length > 0 ? chunks : [text];
  }

  private applyConfigToUtterance(utterance: SpeechSynthesisUtterance) {
    utterance.rate = this.config.rate;
    utterance.pitch = this.config.pitch;
    utterance.volume = this.config.volume;
    
    if (this.config.voice) {
      const voices = window.speechSynthesis.getVoices();
      const voice = voices.find(v => v.name === this.config.voice);
      if (voice) {
        utterance.voice = voice;
      }
    }
  }

  public play() {
    console.log('🎵 Play called - Current state:', this.state);
    
    if (!this.text) {
      this.state.error = 'No text to play';
      console.error('❌ No text to play');
      this.updateState();
      return;
    }

    // Check if Speech Synthesis is supported
    if (!window.speechSynthesis) {
      this.state.error = 'Speech Synthesis not supported in this browser';
      console.error('❌ Speech Synthesis not supported');
      this.updateState();
      return;
    }

    // Handle resume from pause
    if (this.state.isPaused) {
      if (this.utterance && window.speechSynthesis.speaking) {
        // Resume from pause (browser hasn't cancelled it)
        console.log('▶️ Resuming from pause via speechSynthesis.resume()');
        try {
          window.speechSynthesis.resume();
          this.state.isPaused = false;
          this.state.isPlaying = true;
          this.state.isStopped = false;
          this.wasPlayingBeforeHidden = false;
          this.startProgressTracking();
          this.updateState();
          return;
        } catch (error) {
          console.warn('⚠️ Resume failed, will restart:', error);
        }
      }
      
      // If utterance was cancelled by browser, restart from saved position
      console.log('🔄 Browser cancelled utterance, restarting from saved position:', this.pausedTime);
      this.state.isPaused = false;
      this.utterance = null;
      // Continue to start new playback from saved position below
    }

    // Start new playback (or resume from saved position)
    console.log('▶️ Starting new playback from position:', this.pausedTime);
    window.speechSynthesis.cancel(); // Clean up any existing speech
    
    // Split text into chunks for better reliability
    if (this.chunks.length === 0) {
      this.chunks = this.splitIntoChunks(this.text);
      console.log('📦 Split text into', this.chunks.length, 'chunks');
    }
    
    // Calculate which chunk to start from based on saved position
    if (this.pausedTime > 0) {
      const progressRatio = this.pausedTime / this.state.duration;
      this.currentChunkIndex = Math.floor(progressRatio * this.chunks.length);
      console.log('⏩ Resuming from chunk', this.currentChunkIndex, 'of', this.chunks.length);
    } else {
      this.currentChunkIndex = 0;
    }
    
    this.speakCurrentChunk();
  }

  private speakCurrentChunk() {
    if (this.currentChunkIndex >= this.chunks.length) {
      console.log('✅ All chunks completed');
      this.state.isPlaying = false;
      this.state.isPaused = false;
      this.state.isStopped = true;
      this.state.progress = 100;
      this.state.currentWordIndex = this.words.length - 1;
      this.stopProgressTracking();
      this.saveProgress(this.state.duration, 100);
      this.updateState();
      return;
    }

    const chunkText = this.chunks[this.currentChunkIndex];
    console.log('🎤 Speaking chunk', this.currentChunkIndex + 1, 'of', this.chunks.length);
    
    this.utterance = new SpeechSynthesisUtterance(chunkText);
    // Apply all config settings consistently using helper method
    this.applyConfigToUtterance(this.utterance);

    // Event handlers
    this.utterance.onstart = () => {
      console.log('🎵 Chunk started');
      this.state.isPlaying = true;
      this.state.isPaused = false;
      this.state.isStopped = false;
      this.state.error = null;
      
      if (this.startTime === 0 || this.currentChunkIndex === 0) {
        this.startTime = Date.now() - (this.pausedTime * 1000);
      }
      
      this.startProgressTracking();
      this.updateState();
    };

    this.utterance.onend = () => {
      console.log('✅ Chunk ended, moving to next');
      this.currentChunkIndex++;
      
      if (this.currentChunkIndex < this.chunks.length && this.state.isPlaying) {
        // Continue to next chunk
        this.speakCurrentChunk();
      } else {
        // All done
        this.state.isPlaying = false;
        this.state.isPaused = false;
        this.state.isStopped = true;
        this.state.progress = 100;
        this.stopProgressTracking();
        this.saveProgress(this.state.duration, 100);
        this.updateState();
      }
    };

    this.utterance.onerror = (event) => {
      console.error('❌ Speech error:', event.error);
      
      // Try to recover by moving to next chunk
      if (event.error === 'interrupted' || event.error === 'canceled') {
        console.log('🔄 Interrupted, this is expected during pause/stop');
        return;
      }
      
      this.state.error = `Speech error: ${event.error}`;
      this.state.isPlaying = false;
      this.state.isStopped = true;
      this.stopProgressTracking();
      this.updateState();
    };

    this.utterance.onboundary = (event) => {
      if (event.name === 'word' && event.charIndex !== undefined) {
        // Calculate global word index across all chunks
        let totalCharsBefore = 0;
        for (let i = 0; i < this.currentChunkIndex; i++) {
          totalCharsBefore += this.chunks[i].length + 1; // +1 for space between chunks
        }
        
        const globalCharIndex = totalCharsBefore + event.charIndex;
        const textUpToWord = this.text.substring(0, globalCharIndex);
        const wordIndex = textUpToWord.split(/\s+/).length - 1;
        
        this.state.currentWordIndex = Math.max(0, Math.min(wordIndex, this.words.length - 1));
        this.notifyWordChange(this.state.currentWordIndex);
        this.updateState(); // ✅ Notify UI of word index change
      }
    };

    window.speechSynthesis.speak(this.utterance);
  }

  public pause() {
    if (!this.state.isPlaying || this.state.isPaused) return;

    console.log('⏸️ Pausing at:', this.state.currentTime);
    
    try {
      window.speechSynthesis.pause();
      this.state.isPaused = true;
      this.state.isPlaying = true; // Keep true for resume
      this.pausedTime = this.state.currentTime;
      this.stopProgressTracking();
      this.saveProgress(this.pausedTime, this.state.progress);
      this.updateState();
    } catch (error) {
      console.error('❌ Pause error:', error);
      this.state.error = 'Failed to pause audio';
      this.updateState();
    }
  }

  public stop() {
    console.log('⏹️ Stopping audio');
    
    // Cancel all speech synthesis
    window.speechSynthesis.cancel();
    
    // Clean up utterance event handlers
    if (this.utterance) {
      this.utterance.onstart = null;
      this.utterance.onend = null;
      this.utterance.onerror = null;
      this.utterance.onboundary = null;
      this.utterance = null;
    }

    // Stop progress tracking
    this.stopProgressTracking();
    
    // Reset state completely
    this.state.isPlaying = false;
    this.state.isPaused = false;
    this.state.isStopped = true;
    this.state.currentWordIndex = 0;
    this.state.currentTime = 0;
    this.state.progress = 0;
    this.pausedTime = 0;
    this.startTime = 0;
    this.wasPlayingBeforeHidden = false;
    this.chunks = [];
    this.currentChunkIndex = 0;
    
    this.updateState();
  }

  public togglePlayPause() {
    if (this.state.isPlaying && !this.state.isPaused) {
      this.pause();
    } else {
      this.play();
    }
  }

  public setRate(rate: number) {
    this.config.rate = Math.max(0.5, Math.min(2, rate));
    
    // Apply rate to current utterance if it exists
    if (this.utterance) {
      this.utterance.rate = this.config.rate;
    }
    
    // If currently playing, restart from current position with new rate
    if (this.state.isPlaying && !this.state.isPaused) {
      const currentTime = this.state.currentTime;
      console.log('🔄 Restarting with new rate from:', currentTime);
      this.stop();
      this.pausedTime = currentTime;
      setTimeout(() => this.play(), 100);
    }
  }

  public setVolume(volume: number) {
    const newVolume = Math.max(0, Math.min(1, volume));
    console.log('🔊 Setting volume to:', newVolume);
    
    this.config.volume = newVolume;
    
    // Apply immediately to current utterance if it exists
    if (this.utterance) {
      this.utterance.volume = newVolume;
      console.log('✅ Applied volume to current utterance');
    }
  }

  public setVoice(voiceName: string) {
    this.config.voice = voiceName;
    // Restart if playing to apply new voice
    if (this.state.isPlaying) {
      const wasPlaying = !this.state.isPaused;
      this.stop();
      if (wasPlaying) {
        setTimeout(() => this.play(), 100);
      }
    }
  }

  public setPitch(pitch: number) {
    this.config.pitch = Math.max(0.5, Math.min(2, pitch));
    // Restart if playing to apply new pitch
    if (this.state.isPlaying) {
      const wasPlaying = !this.state.isPaused;
      this.stop();
      if (wasPlaying) {
        setTimeout(() => this.play(), 100);
      }
    }
  }

  public getAvailableVoices(): SpeechSynthesisVoice[] {
    return window.speechSynthesis?.getVoices() || [];
  }

  public seekTo(time: number) {
    console.log('⏩ Seeking to:', time);
    const wasPlaying = this.state.isPlaying && !this.state.isPaused;
    
    const targetTime = Math.max(0, Math.min(this.state.duration, time));
    this.pausedTime = targetTime;
    this.state.currentTime = targetTime;
    this.state.progress = (targetTime / this.state.duration) * 100;
    
    // Calculate word index and chunk from time
    const progressRatio = targetTime / this.state.duration;
    const wordIndex = Math.floor(progressRatio * this.words.length);
    this.state.currentWordIndex = Math.max(0, Math.min(wordIndex, this.words.length - 1));
    
    // Recalculate chunk position
    if (this.chunks.length > 0) {
      this.currentChunkIndex = Math.floor(progressRatio * this.chunks.length);
    }
    
    // Restart playback from new position if we were playing
    if (wasPlaying) {
      window.speechSynthesis.cancel();
      this.state.isPaused = false;
      this.startTime = 0;
      setTimeout(() => this.play(), 100);
    } else {
      this.saveProgress(targetTime, this.state.progress);
    }
    
    this.updateState();
  }

  private startProgressTracking() {
    this.stopProgressTracking();
    
    this.progressInterval = window.setInterval(() => {
      const elapsed = (Date.now() - this.startTime) / 1000;
      this.state.currentTime = elapsed;
      this.state.progress = Math.min((elapsed / this.state.duration) * 100, 100);
      
      // Auto-save every 5 seconds
      if (Math.floor(elapsed) % 5 === 0) {
        this.saveProgress(elapsed, this.state.progress);
      }
      
      this.updateState();
    }, 100);
  }

  private stopProgressTracking() {
    if (this.progressInterval !== null) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  public getState(): AudioState {
    return { ...this.state };
  }

  public getConfig(): AudioConfig {
    return { ...this.config };
  }

  public getWords(): string[] {
    return [...this.words];
  }

  public onStateChange(callback: StateChangeCallback) {
    this.stateCallbacks.push(callback);
    return () => {
      this.stateCallbacks = this.stateCallbacks.filter(cb => cb !== callback);
    };
  }

  public onWordChange(callback: WordChangeCallback) {
    this.wordCallbacks.push(callback);
    return () => {
      this.wordCallbacks = this.wordCallbacks.filter(cb => cb !== callback);
    };
  }

  private updateState() {
    this.stateCallbacks.forEach(cb => cb(this.state));
  }

  private notifyWordChange(wordIndex: number) {
    this.wordCallbacks.forEach(cb => cb(wordIndex));
  }

  public destroy() {
    this.stop();
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    this.stateCallbacks = [];
    this.wordCallbacks = [];
  }
}

export const createAudioEngine = () => new UnifiedAudioEngine();
